import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const KEY_RE = /^[a-z_][a-z0-9_]*$/;

function emptyVar() {
  return { key: "", label: "", default: "", required: true, description: "" };
}

export function VariableEditor({ value = [], onChange }) {
  function update(i, patch) {
    const next = value.map((v, idx) => (idx === i ? { ...v, ...patch } : v));
    onChange?.(next);
  }
  function add() {
    onChange?.([...value, emptyVar()]);
  }
  function remove(i) {
    onChange?.(value.filter((_, idx) => idx !== i));
  }
  return (
    <div className="space-y-3" data-testid="variable-editor">
      {value.length === 0 && (
        <p className="text-sm text-slate-400" data-testid="variables-empty">
          No variables yet. Add one to parameterise this workflow.
        </p>
      )}
      {value.map((v, i) => {
        const keyValid = !v.key || KEY_RE.test(v.key);
        return (
          <div
            key={i}
            data-testid={`variable-row-${i}`}
            className="rounded-lg border border-slate-200 bg-white p-3"
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
              <div className="md:col-span-3">
                <Label className="text-xs text-slate-500" htmlFor={`var-key-${i}`}>
                  Key (snake_case)
                </Label>
                <Input
                  id={`var-key-${i}`}
                  data-testid={`variable-key-${i}`}
                  value={v.key}
                  onChange={(e) => update(i, { key: e.target.value })}
                  placeholder="supplier_name"
                  className={!keyValid ? "border-red-400 focus-visible:ring-red-400" : ""}
                />
                {!keyValid && (
                  <p className="mt-1 text-[11px] text-red-600" data-testid={`variable-key-${i}-error`}>
                    Use only a-z, 0-9, _
                  </p>
                )}
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs text-slate-500" htmlFor={`var-label-${i}`}>
                  Label
                </Label>
                <Input
                  id={`var-label-${i}`}
                  data-testid={`variable-label-${i}`}
                  value={v.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                  placeholder="Supplier"
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs text-slate-500" htmlFor={`var-default-${i}`}>
                  Default value
                </Label>
                <Input
                  id={`var-default-${i}`}
                  data-testid={`variable-default-${i}`}
                  value={v.default}
                  onChange={(e) => update(i, { default: e.target.value })}
                  placeholder="Acme Office Supplies"
                />
              </div>
              <div className="flex items-center gap-2 md:col-span-2">
                <Checkbox
                  id={`var-required-${i}`}
                  data-testid={`variable-required-${i}`}
                  checked={!!v.required}
                  onCheckedChange={(c) => update(i, { required: !!c })}
                />
                <Label
                  htmlFor={`var-required-${i}`}
                  className="text-sm text-slate-700"
                >
                  Required
                </Label>
              </div>
              <div className="flex items-end justify-end md:col-span-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(i)}
                  data-testid={`variable-remove-${i}`}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          </div>
        );
      })}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        data-testid="variable-add-button"
      >
        <Plus size={14} className="mr-1.5" />
        Add variable
      </Button>
    </div>
  );
}
